import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { getToken, removeTokens, refreshTokens } from '../services/api';

// Create Auth Context
const AuthContext = createContext({});

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Set up automatic token refresh every 14 minutes
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Only set up refresh if user is authenticated
    if (user && getToken()) {      
      refreshIntervalRef.current = setInterval(async () => {
        try {
          const refreshed = await refreshTokens();
          if (refreshed) {
            console.log('Token refreshed successfully');
          } else {
            logout();
          }
        } catch (error) {
          console.error('Error during automatic token refresh:', error);
          logout();
        }
      }, 14 * 60 * 1000); // 14 minutes in milliseconds
    }

    // Cleanup function
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [user]);

  // Check authentication status
  const checkAuth = async () => {
    try {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      } else {
        removeTokens();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      removeTokens();
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      const result = await authService.login(email, password);
      
      if (result.success) {
        setUser(result.user);
        
        // Also trigger an immediate token refresh to ensure we have a fresh token
        // This helps synchronize the refresh cycle
        setTimeout(async () => {
          try {
            await refreshTokens();
          } catch (error) {
            console.error('Error during initial token refresh:', error);
          }
        }, 1000); // Small delay to ensure login is fully processed
        
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = error.message || 'Erro ao fazer login';
      setError(message);
      return { success: false, message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Clear the refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      
      await authService.logout();
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Update user in context
  const updateUser = (userData) => {
    setUser(userData);
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isAssociado: user?.role === 'associado',
    isComercial: user?.role === 'comercial',
    isContabil: user?.role === 'contabilidade',
    isDiretoriaGeral: user?.role === 'diretoria_geral',
    isDiretoriaJuridica: user?.role === 'diretoria_juridica',
    isDiretoriaComercial: user?.role === 'diretoria_comercial',
    isGestorComercial: user?.role === 'gestor_comercial',
    isGestorJuridico: user?.role === 'gestor_juridico',
    isJuridico: user?.role === 'juridico',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
